import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { Me } from 'src/providers/me/me';

import { Loading } from 'src/shared/components/loading/loading';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { TidyCurrencyPipe } from 'src/shared/pipes/tidy-currency.pipe';

import { WindowService } from 'src/shared/providers/window.service';
import { TimeoutableComponent } from 'src/shared/components/timeoutable/timeoutable.component';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { FeatureGateService } from 'src/shared/services/feature-gate.service';
import { SubscriptionChangeDetails, SubscriptionGateService } from 'src/shared/services/subscription-gate.service';
import { CLEANING_MAINTENANCE_PER_UNIT, RESERVATION_FEE_PERCENT } from 'src/shared/components/subscription-plan-card/subscription-plan-card.component';
import { SuccessPage } from 'src/shared/pages/success/success';
import { ChangeSubscriptionPage } from './change-subscription/change-subscription';
import { EditSubscriptionPage } from './edit-subscription/edit-subscription';
import { GetHelpPage } from '../concierge/get-help/get-help';
import { EnableAutopilotComponent } from '../go-live/enable-autopilot/enable-autopilot.component';

@Component({
    templateUrl: 'subscription.html',
    standalone: false
})

export class SubscriptionsPage extends TimeoutableComponent implements OnInit {

  billableAddresses: number;
  bill: number;
  currentSubscription: any;
  errorMessage: string;
  nextSubscription: any;
  subscriptionInvoices: any;
  subscriptionType: string;
  subscriptionDisplayName: string;
  hasReservationFeePlan: boolean;
  reservationFeePercent: number;
  reservationMinimumCents: number;
  baseMinimumPerUnit: number;
  cleaningMaintenancePerUnit: number;
  cleaningMaintenanceOn: boolean;
  nextChargeNote: string | null;
  isRightSideContent = true;
  dialogParams: any;
  hasNegativeBalance: boolean;
  isAccountSuspended: boolean;

  constructor(
    private me: Me,
    private modalCtrl: ModalController,
    public windowService: WindowService,
    private tidyCurrencyPipe: TidyCurrencyPipe,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    private subscriptionGate: SubscriptionGateService,
    private featureGate: FeatureGateService
  ) {
    super();
  }

  @Loading('', true)
  async ngOnInit() {
    try {
      this.rightSidePanelService.setDialogLoading(true);
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      this.rightSidePanelService.setDialogPageTitle('Subscription');
      if (this.isRightSideContent) {
        this.dialogParams = await this.storage.retrieve('dialog-params');
      }
      this.loaded = false;
      const [hasNegativeBalance, me, subscriptions, subscriptionInvoices] = await Promise.all([
        this.me.getHasNegativeBalance(),
        this.me.load(true),
        this.me.getSubscriptions(),
        this.me.getSubscriptionInvoices()
      ]);
      this.hasNegativeBalance = hasNegativeBalance;
      this.isAccountSuspended = me.suspended_at ? true : false;
      this.subscriptionInvoices = subscriptionInvoices;
      this.filterSubscriptions(subscriptions);
      const debitByAddress = this.currentSubscription?.plan?.debit_strategies?.debit_by_address;
      const freeAddresses = debitByAddress?.number_of_free_addresses || 0;
      this.billableAddresses = Math.max((this.currentSubscription?.customer_address_count || 0) - freeAddresses, 0);
      this.subscriptionType = this.currentSubscription?.plan?.renewal_frequency;
      this.subscriptionDisplayName = this.me.getSubscriptionType(this.currentSubscription?.plan?.key);
      this.bill = (debitByAddress?.amount_by_address || 0) * this.billableAddresses;
      // New rental plans (Trello 18573) bill 3.9% of reservation revenue at guest checkout.
      // billing_api names the strategy `debit_fee_by_guest_reservation` (fee_percent +
      // minimum_amount_by_address in cents); the older name is kept as a fallback. Plans
      // with a debit_by_address strategy also carry the C&M $/unit/mo add-on.
      const strategies = this.currentSubscription?.plan?.debit_strategies;
      const reservationFee = strategies?.debit_fee_by_guest_reservation || strategies?.debit_fee_by_reservation;
      this.hasReservationFeePlan = !!reservationFee;
      this.reservationFeePercent = reservationFee?.fee_percent ?? RESERVATION_FEE_PERCENT;
      this.reservationMinimumCents = reservationFee?.minimum_amount_by_address || 1900;
      // Feeds the shared plan card, which quotes dollars — the API bills in cents. An
      // account without the add-on has no debit_by_address, so it falls back to list price.
      this.baseMinimumPerUnit = this.reservationMinimumCents / 100;
      this.cleaningMaintenancePerUnit = debitByAddress?.amount_by_address
        ? debitByAddress.amount_by_address / 100
        : CLEANING_MAINTENANCE_PER_UNIT;
      this.cleaningMaintenanceOn = !!debitByAddress;
      this.nextChargeNote = this.currentSubscription?.next_charge_on
        ? `Next charge: ${  new ParseDateTimePipe().transform(this.currentSubscription.next_charge_on, 'MMM d yyyy')}`
        : null;
      this.loaded = true;
      this.rightSidePanelService.setDialogLoading(false);
    } catch (err) {
      this.timeoutHandler(err);
    }
  }

  filterSubscriptions(subscriptions) {
    if (subscriptions.length > 1) {
      subscriptions.map((subscription) => {
        if (subscription.is_activated_in_next_cycle) {
          this.nextSubscription = subscription;
        } else {
          this.currentSubscription = subscription;
        }
      });
    } else {
      this.currentSubscription = subscriptions[0];
    }
    if (this.currentSubscription) {
      this.currentSubscription.invoice = this.subscriptionInvoices.find(subInvoice => subInvoice?.subscription?.id === this.currentSubscription.id);
    }
  }

  goToContactConcierge() {
    this.rightSidePanelService.navigateTo('get-help', {}, GetHelpPage);
  }

  goToChangeSubscriptionPage() {
    this.rightSidePanelService.setDialogPageTitle(this.hasNegativeBalance ? 'Subscription Payment Issue' : 'Change Subscription');
    const params = {
      currentSubscription: this.currentSubscription,
      nextSubscription: this.nextSubscription,
      billableAddresses: this.billableAddresses
    }
    this.rightSidePanelService.navigateTo('change-subscription', params, ChangeSubscriptionPage);
  }

  goToSubscribe() {
    this.rightSidePanelService.setDialogPageTitle('Subscribe');
    this.rightSidePanelService.navigateTo('go-live/enable-autopilot', {}, EnableAutopilotComponent);
  }

  goToEditSubscription() {
    this.rightSidePanelService.setDialogPageTitle('Edit Subscription');
    this.rightSidePanelService.navigateTo('subscription/edit-subscription', {}, EditSubscriptionPage);
  }

  // mat-slide-toggle has already moved the switch by the time this fires, so a prompt the
  // client backs out of has to write the old value back to snap it into place.
  async onCleaningMaintenanceToggled(on: boolean) {
    const changed = on
      ? await this.subscriptionGate.promptUpgrade(this.planChangeDetails())
      : await this.subscriptionGate.promptRemoveCleaningMaintenance(this.planChangeDetails());
    if (changed) {
      await this.ngOnInit();
    } else {
      this.cleaningMaintenanceOn = !on;
    }
  }

  // WiFi + Noise Sensor and The Box aren't self-serve — same account-manager prompt the
  // subscribe page shows.
  gateHardwareAddOn() {
    this.featureGate.gate();
  }

  // The prompt quotes what this account actually pays, not the list price: discounted
  // `full-autopilot-cleaning-maintenance-35` … `-0` plans carry their own per-unit amount.
  private planChangeDetails(): SubscriptionChangeDetails {
    return {
      perUnitDollars: this.cleaningMaintenancePerUnit,
      feePercent: this.reservationFeePercent
    };
  }

}
